import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ArticleDetail {
  readonly articleId: string;
  readonly title: string;
  readonly category: string;
  readonly status: string;
  readonly versionNo: number;
  readonly authorName: string;
  readonly bodyMarkdown: string;
  readonly updatedAt: string | null;
}

/**
 * GR L27.02 — Article Reader (`/kb/articles/{id}`). Renders a single
 * `kb_articles` record with its current `kb_article_versions.body_markdown`,
 * category, author and version. Read-only reader surface for the KB.
 */
export function GrKbArticlePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { articleId } = useParams<{ articleId: string }>();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ArticleDetail }>(`/v1/collab/knowledge/${articleId}`, { token })
      .then((r) => { if (alive) { setArticle(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, articleId]);

  return (
    <main aria-label="kb-article">
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr27.kbArticle.loading')}</p> : article && (
        <article>
          <h1 aria-label="article-title">{article.title}</h1>
          <p aria-label="article-meta">
            {article.category} · v{article.versionNo} · {article.authorName} · {article.status}
          </p>
          <div aria-label="article-body">{article.bodyMarkdown}</div>
        </article>
      )}
    </main>
  );
}
